import { useRef, useState, type FormEvent, type PointerEvent } from 'react';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface ProgressBarProps {
  currentPage: number;
  totalPages: number;
  onJump: (page: number) => void;
  className?: string;
}

/**
 * Bottom-bar slim progress bar with click/drag-to-jump and a small "go to
 * page" form. The pointer's fractional position on the bar maps to a page.
 * While dragging, the bar previews the target page locally and only calls
 * `onJump` on release, so scrubbing doesn't resolve every page in between.
 */
export function ProgressBar({ currentPage, totalPages, onJump, className }: ProgressBarProps) {
  const [draft, setDraft] = useState('');
  const [dragPage, setDragPage] = useState<number | null>(null);
  const jumpInputRef = useRef<HTMLInputElement | null>(null);

  function clampPage(page: number): number {
    return Math.max(1, Math.min(totalPages, page));
  }

  function pageAt(e: PointerEvent<HTMLDivElement>): number | null {
    const rect = e.currentTarget.getBoundingClientRect();
    if (rect.width === 0 || totalPages <= 0) return null;
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    return clampPage(Math.floor(ratio * totalPages) + 1);
  }

  function handlePointerDown(e: PointerEvent<HTMLDivElement>) {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const page = pageAt(e);
    if (page === null) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragPage(page);
  }

  function handlePointerMove(e: PointerEvent<HTMLDivElement>) {
    if (dragPage === null) return;
    const page = pageAt(e);
    if (page !== null) setDragPage(page);
  }

  function handlePointerUp(e: PointerEvent<HTMLDivElement>) {
    if (dragPage === null) return;
    const page = pageAt(e) ?? dragPage;
    setDragPage(null);
    onJump(page);
  }

  function handlePointerCancel() {
    setDragPage(null);
  }

  function handleJump(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const n = Number.parseInt(draft, 10);
    if (Number.isFinite(n) && totalPages > 0) {
      onJump(clampPage(n));
      jumpInputRef.current?.blur();
    }
    setDraft('');
  }

  const dragging = dragPage !== null;
  const shownPage = dragPage ?? currentPage;
  const pct = totalPages > 0 ? (shownPage / totalPages) * 100 : 0;

  return (
    <footer
      className={cn(
        'sticky bottom-0 z-40 flex flex-col gap-1 border-t border-border/60 bg-background/95 px-2 pb-[calc(0.375rem_+_var(--app-safe-area-bottom))] pt-1.5 backdrop-blur',
        className,
      )}
    >
      <div
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        onLostPointerCapture={handlePointerCancel}
        role="slider"
        aria-valuenow={shownPage}
        aria-valuemin={1}
        aria-valuemax={totalPages}
        aria-label="Reading progress"
        tabIndex={0}
        className="group relative cursor-pointer touch-none select-none py-1.5"
      >
        <div className="h-2 rounded-full bg-muted">
          <div
            className={cn('h-full rounded-full bg-primary', !dragging && 'transition-[width]')}
            style={{ width: `${pct}%` }}
          />
        </div>
        <div
          aria-hidden
          className={cn(
            'pointer-events-none absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-background bg-primary shadow transition-opacity',
            dragging ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
          )}
          style={{ left: `${pct}%` }}
        />
        {dragging && (
          <div
            data-testid="progress-drag-preview"
            className="pointer-events-none absolute bottom-full mb-1 -translate-x-1/2 rounded bg-primary px-1.5 py-0.5 text-xs font-medium tabular-nums text-primary-foreground shadow"
            style={{ left: `${pct}%` }}
          >
            {dragPage}
          </div>
        )}
      </div>
      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>
          {shownPage} / {totalPages}
        </span>
        <form onSubmit={handleJump} className="flex items-center gap-1">
          <Input
            ref={jumpInputRef}
            aria-label="Jump to page"
            inputMode="numeric"
            min={1}
            max={totalPages}
            value={draft}
            onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ''))}
            placeholder="Jump…"
            className="h-7 w-20 text-base sm:text-xs"
          />
          <Button size="sm" variant="ghost" type="submit">
            Go
          </Button>
        </form>
      </div>
    </footer>
  );
}
